import { IsIn, IsInt, IsOptional, IsString, Matches, Min } from 'class-validator';

/**
 * PD-17406 — the storefront's report that a sign-in step could not reach one
 * of our servers (`POST /auth/portal/client-report` on the login server).
 *
 * The failure it exists for: the login server issued the shopper a token, but
 * the follow-up request to crm-api (`/auth/me`) never left the device because
 * something on the shopper's network blocked it. crm-api never saw that
 * request, so without this report the only trace was the shopper's own
 * screen. The login server is reachable in that case (it just answered), so
 * the storefront tells it, and it writes one log line.
 */

/** The sign-in steps a report can name, in the order a sign-in runs them. */
export const PORTAL_SIGN_IN_STEPS = [
	'login',
	'code-request',
	'code-verify',
	'sign-in-link',
	'token-refresh',
	'auth-me',
] as const;

export type PortalSignInStep = (typeof PORTAL_SIGN_IN_STEPS)[number];

/** The two servers a storefront request goes to. */
export const PORTAL_NETWORK_SERVICES = ['login', 'crm-api'] as const;

export type PortalNetworkService = (typeof PORTAL_NETWORK_SERVICES)[number];

export class PortalClientReportRequestDto {
	@IsIn(PORTAL_SIGN_IN_STEPS)
	public step!: PortalSignInStep;

	@IsIn(PORTAL_NETWORK_SERVICES)
	public service!: PortalNetworkService;

	/**
	 * The storefront address label the request carried as `X-Portal-Tenant`
	 * (`<brand>--<tenant>`, or just `<tenant>`). Absent on a custom domain.
	 * Restricted to the characters such a label can hold, so whatever is sent
	 * here cannot break up or forge the log line it is written to.
	 */
	@IsOptional()
	@IsString()
	@Matches(/^[A-Za-z0-9_-]{1,120}$/)
	public portalKey?: string;

	/** The storefront's portal_site id from its start-up response. */
	@IsOptional()
	@IsInt()
	@Min(1)
	public portalSiteId?: number;
}

export interface PortalClientReportResponseDto {
	success: boolean;
}
